/* eslint-disable no-unused-expressions */
/* eslint-disable react-hooks/exhaustive-deps */
import { classnames, Icon } from "@culturehq/components";
import { useIsMobile } from "app/hooks";
import { Trans } from "app/utils";
import LazyInputRange from "components/survey/item-types/LazyInputRange";
import React, { useEffect, useRef, useState } from "react";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";

type PrompterProps = {
    editableScript: string;
    setEditableScript: React.Dispatch<React.SetStateAction<string>>;
    setShowScript: (show: boolean) => void;
    showScript: boolean;
  };

const Prompter: React.FC<PrompterProps> = (
  { setShowScript, editableScript, setEditableScript, showScript }) => {
  const isMobile = useIsMobile();
  const PLACEHOLDER_TEXT = "Type your script here...";
  const RANGE_MIN_VALUE = 10;
  const SPEED_INITIAL_VALUE = 0.5;
  const containerRef = useRef<HTMLDivElement>(null);
  const [displaySpeedBar, setDisplaySpeedBar] = useState(false);
  // from 0.5 to 3 pxs
  const [scrollSpeed, setScrollSpeed] = useState(1.75);
  const [play, setPlay] = useState(false);
  const [fontSize, setFontSize] = useState(60);
  const [grabbing, setGrabbing] = useState(false);
  const [resizing, setResizing] = useState(false);
  const resizableRef = useRef<HTMLDivElement>(null);
  const [customScript, setCustomScript] = useState(editableScript || "");
  const initialWidth = isMobile ? 350 : 650;
  const initialHeight = isMobile ? 200 : 280;

  const minWidth = Math.round(initialWidth * 0.6615);
  const maxWidth = Math.round(initialWidth * (isMobile ? 1.2 : 1.8462));

  const minHeight = Math.round(initialHeight * 0.5714);
  const maxHeight = Math.round(initialHeight * 2.1429);

  // size
  const [dimensions, setDimensions] = useState({
    width: initialWidth, height: initialHeight });

  // position
  const [position, setPosition] = useState({
    x: 0, y: 0
  });

  useEffect(() => {
    if (showScript) {
      setCustomScript(editableScript);
    }
  }, [showScript]);

  useEffect(() => {
    if (containerRef.current) {
      // if the script is empty place the cursor at the beginning of the placeholder
      if (editableScript.trim() === "") {
        const container = containerRef.current;
        container.innerHTML = "";

        const range = document.createRange();
        const selection = window.getSelection();
        range.setStart(container, 0);
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  }, [editableScript]);

  useEffect(() => {
    setCustomScript(editableScript);
    if (containerRef.current) {
      const moveCursorAtEnd = (container: HTMLDivElement) => {
        const moveAfterRender = () => {
          if (!container.lastChild) {
            requestAnimationFrame(moveAfterRender);
            return;
          }

          const range = document.createRange();
          const selection = window.getSelection();
          range.setStart(container, container.childNodes.length);
          range.collapse(true);
          selection?.removeAllRanges();
          selection?.addRange(range);
          // eslint-disable-next-line no-param-reassign
          container.scrollTop = container.scrollHeight;
        };

        // if the script is not empty place the cursor at the end of the script text
        requestAnimationFrame(moveAfterRender);
      };
      containerRef.current.focus();
      moveCursorAtEnd(containerRef.current);
    }
  }, []);

  useEffect(() => {
    const scrollContainer = containerRef.current;
    if (!scrollContainer) return;
    const scrollPosition = scrollContainer.scrollTop + scrollContainer.clientHeight;
    if (play && scrollPosition === scrollContainer.scrollHeight) {
      scrollContainer.scrollBy(0, -(scrollContainer.scrollHeight));
    }
    const scrollInterval = setInterval(() => {
      if (scrollContainer.scrollTop + scrollContainer.clientHeight < scrollContainer.scrollHeight
        && play) {
        scrollContainer.scrollBy(0, scrollSpeed);
      } else {
        clearInterval(scrollInterval);
        setPlay(false);
      }
    }, 30);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(scrollInterval);
  }, [play, scrollSpeed]);

  useEffect(() => {
    window.addEventListener("mousemove", handleMovement);
    window.addEventListener("touchmove", handleMovement);

    window.addEventListener("mouseup", handleMoveEnd);
    window.addEventListener("touchend", handleMoveEnd);

    return () => {
      window.removeEventListener("mousemove", handleMovement);
      window.removeEventListener("touchmove", handleMovement);

      window.removeEventListener("mouseup", handleMoveEnd);
      window.removeEventListener("touchend", handleMoveEnd);
    };
  }, [resizing]);

  const handleStartMove = () => {
    setResizing(true);
  };

  const handleMovement = (e: any) => {
    if (!resizing || !resizableRef.current) return;
    const rect = resizableRef.current.getBoundingClientRect();
    const xMovement = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    const yMovement = e.type.includes("touch") ? e.touches[0].clientY : e.clientY;

    const newWidth = Math.min(xMovement - rect.left, maxWidth);
    const newHeight = Math.min(yMovement - rect.top, maxHeight);

    setDimensions(_ => ({
      width: Math.max(minWidth, newWidth),
      height: Math.max(minHeight, newHeight)
    }));
  };

  const handleMoveEnd = () => {
    setResizing(false);
  };

  const handleInput = (e: any) => {
    if (containerRef.current) {
      const scriptText = containerRef.current.innerText;
      if (scriptText.trim() === "") {
        containerRef.current.innerHTML = "";
      }
      setEditableScript(scriptText);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const handleStop = (_: DraggableEvent, data: DraggableData) => {
    setPosition({
      x: data.x, y: data.y
    });
  };

  if (!showScript) {
    return (<></>);
  }

  return (
    <Draggable
      handle=".drag-button"
      bounds={{ top: -100, left: -600, right: 600, bottom: 600 }}
      defaultPosition={{ x: position.x, y: position.y }}
      onStop={handleStop}
    >
      <div
        className="prompter"
        style={{
          height: `${dimensions.height}px`,
          width: `${dimensions.width}px`
        }}
        ref={resizableRef}
      >
        <div className="prompter-header">
          <div className="right-side">
            <div
              className={classnames("drag-button", grabbing ? "drag-button__grabbing" : "")}
              onMouseDown={() => {
                setGrabbing(true);
              }}
              onMouseUp={() => {
                setGrabbing(false);
              }}
              onMouseLeave={() => {
                setGrabbing(false);
              }}
            >
              <Icon
                icon="view-grid"
              />
            </div>
            <div className="close-button">
              <Icon
                onClick={() => {
                  setShowScript(false);
                }}
                icon="close"
              />
            </div>
          </div>
        </div>
        <div
          className={classnames("prompter-script", editableScript.trim() === "" ? "prompter-script__placeHolder" : "")}
          ref={containerRef}
          style={{ fontSize, height: dimensions.height }}
          contentEditable
          suppressContentEditableWarning
          data-placeholder={PLACEHOLDER_TEXT}
          onInput={handleInput}
          onPaste={handlePaste}
        >
          {customScript}
        </div>
        <div className="prompter-menu">
          <div className="icon-left">
            {!displaySpeedBar
          && (
            <>
              <Icon
                onClick={() => {
                  setDisplaySpeedBar(true);
                }}
                icon="clock-filled"
              />
            </>
          )}
            {displaySpeedBar
          && (
            <div className="speed-bar" onMouseLeave={() => setDisplaySpeedBar(false)}>
              <LazyInputRange
                maxValue={60}
                minValue={10}
                value={scrollSpeed * (RANGE_MIN_VALUE / SPEED_INITIAL_VALUE)}
                onChange={(speed: any) => {
                  setScrollSpeed(speed * (SPEED_INITIAL_VALUE / RANGE_MIN_VALUE));
                }}
              />
            </div>
          )}
          </div>
          <div className="play-button">
            <Icon
              onClick={() => {
                setPlay(!play);
              }}
              icon={play ? "pause-no-bg" : "play-no-border"}
            />
          </div>
          <div className="icon-right">
            <Icon
              className={fontSize === 40 ? "disabled" : ""}
              onClick={() => {
                setFontSize(prev => (prev === 40 ? prev : prev - 2));
              }}
              icon="minus"
            />
            <Trans>Aa</Trans>
            <Icon
              className={fontSize === 80 ? "disabled" : ""}
              onClick={() => {
                setFontSize(prev => (prev === 80 ? prev : prev + 2));
              }}
              icon="plus"
            />
          </div>
          <div
            className="resize-handle"
            onMouseDown={handleStartMove}
            onTouchMove={handleStartMove}
          />
        </div>
      </div>
    </Draggable>
  );
};

export default Prompter;
